/* eslint-disable react-hooks/exhaustive-deps */
import React, { FC, useCallback, useMemo, useState, useEffect } from 'react';
import { Grid } from '@material-ui/core';
import moment from 'moment';
import { DateRange } from '@material-ui/pickers';
import { formatTimeToCY } from 'src/utils';
import { useStyles } from './styles';
import { useApiError, useAppData, useLoading } from '../../../providers';
import { Header, PageTitle, ReportTitle, ReportsGeneralToolbar } from '../../../components';
import { ClosedPositionItem } from './ClosedPositionsModel';
import { getClosedPositions } from './ClosedPositionsService';
import { trackAmplitudeData } from '../../../services/amplitudeService';
import { useCommonStyles } from '../../../styles';
import { ReportTable } from './components';
import { ColumnConfig } from '../DataTable';
import { formatWithoutRounding } from '../DataTable/formatters';

export const ClosedPositions: FC = () => {
  const classes = useStyles();
  const commonClasses = useCommonStyles();
  const { userData, translations } = useAppData();
  const { addError } = useApiError();
  const { setIsLoading } = useLoading();
  const [selectedDateRange, setSelectedDateRange] = useState<DateRange<Date>>([moment().subtract(1, 'months').toDate(), moment().subtract(1, 'days').toDate()]);
  const [reportData, setReportData] = useState<ClosedPositionItem[]>([]);
  const [isReportReady, setIsReportReady] = useState<boolean>(false);
  const [translation, setTranslation] = useState<any>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([]);

  useEffect(() => {
    setTranslation(JSON.parse(translations));
  }, [translations]);

  const columns = useMemo(
    (): ColumnConfig<ClosedPositionItem>[] => [
      { key: 'login', label: translation.text_6595, type: 'string' },
      { key: 'partnershipCode', label: translation.text_6584, type: 'string' },
      { key: 'openTime', label: translation.text_6585, type: 'date', format: (v) => formatTimeToCY(v) },
      { key: 'positionType', label: translation.text_6586, type: 'string' },
      { key: 'symbol', label: translation.text_6587, type: 'string' },
      { key: 'closeTime', label: translation.text_6589, type: 'date', format: (v) => formatTimeToCY(v) },
      { key: 'openPrice', label: translation.text_6588, type: 'number', format: (v) => formatWithoutRounding(v) },
      { key: 'closePrice', label: translation.text_6590, type: 'number', format: (v) => formatWithoutRounding(v) },
      { key: 'volumeUSD', label: translation.text_6591, type: 'number', format: (v) => formatWithoutRounding(v), summable: true },
      { key: 'broker', label: translation.text_6592, type: 'string' },
      { key: 'regulator', label: translation.text_6593, type: 'string' },
      { key: 'accountType', label: translation.text_6594, type: 'string' },
    ],
    [translation],
  );

  useEffect(() => {
    setSelectedColumns(columns.map((col) => col.label));
  }, [columns]);

  const getReportData = useCallback(
    async (email: string) => {
      setIsLoading(true);
      try {
        trackAmplitudeData('partnerPortal_action_report', {
          type: 'Requested',
          report_name: 'Closed Positions',
        });

        const response = await getClosedPositions(email, moment(selectedDateRange[0]).format('YYYY-MM-DD'), moment(selectedDateRange[1]).format('YYYY-MM-DD'));
        setReportData(response.data.reportClosedPositions);
        setIsReportReady(true);
      } catch (error) {
        addError(JSON.stringify(error.response));
      } finally {
        setIsLoading(false);
      }
    },
    [addError, selectedDateRange],
  );

  const handleNewReportRequest = useCallback(async () => {
    setIsReportReady(false);
    const userEmail: string = JSON.parse(userData)?.userEmail;
    await getReportData(userEmail);
  }, [userData, getReportData]);

  return (
    <>
      <Header title={translation.text_6663} />
      <Grid container direction="column" className={classes.root}>
        <PageTitle title={translation.text_6663} />
        <Grid container direction="column" className={commonClasses.mainContainer}>
          <ReportTitle
            title={translation.text_6425}
            subTitle={translation.closed_position_tooltip}
            exportFileName={translation.text_6425}
            columns={columns}
            allData={reportData}
            selectedColumns={selectedColumns}
            enableButtons={isReportReady}
            fontSize={6}
            translation={translation}
            reportNameInEn="Closed Positions"
          />
          <Grid container direction="column">
            <ReportsGeneralToolbar translation={translation} dateRange={selectedDateRange} handleDateRangeChange={(value) => setSelectedDateRange(value)} showButton handleButtonClick={handleNewReportRequest} />
            <Grid item xs={12}>
              <ReportTable rows={reportData} translation={translation} columns={columns} selectedColumns={selectedColumns} onSelectColumns={setSelectedColumns} />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};
